import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCcw, XCircle, RotateCw, Loader2, BookmarkPlus, Send, Eye, X, ShieldOff } from 'lucide-react';
import { inviteApi } from '../../lib/inviteApi';
import { useSearchProgressShared } from '../../lib/SearchProgressContext';
import InviteStatusBadge from '../../components/invite/InviteStatusBadge';
import SearchResultProfile from '../../components/invite/SearchResultProfile';

const LIST_POLL_MS = 4000;
const PRIORITY_RANK = { vip: 4, high: 3, normal: 2, low: 1 };
const DUP_RANK = { new: 5, possible_duplicate: 4, existing_contact: 3, exact_email: 2, exact_social: 2, exact_website: 2, already_invited: 1, already_ticketed: 1, suppressed: 0 };

export default function SearchResultsTab({ exhibitionId }) {
  const [searches, setSearches] = useState([]);
  const [active, setActive] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [showMoveAll, setShowMoveAll] = useState(false);
  const [moveAllResult, setMoveAllResult] = useState(null);
  const [rowBusy, setRowBusy] = useState({});  // { result_id: 'moving' | 'converting' }
  const timerRef = useRef(null);
  const sharedSearch = useSearchProgressShared();
  // Track per-search qualification toast IDs so we can swap the loading toast
  // for the terminal-state toast (success / failure) once polling detects it.
  const qualifyToastsRef = useRef({}); // { [searchId]: { toastId, lastStatus } }

  const refreshList = async () => {
    if (!exhibitionId) return;
    try {
      const list = await inviteApi.listSearches(exhibitionId);
      setSearches(list);
      setActive((prev) => {
        if (prev) {
          const found = list.find((x) => x.id === prev.id);
          return found || prev;
        }
        return list[0] || null;
      });
    } catch {
      /* never block tab on transient error */
    } finally { setLoading(false); }
  };

  const loadDetails = async (id) => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const d = await inviteApi.getSearch(id);
      setDetails(d);
      setSearches((prev) => prev.map((s) => (s.id === id ? { ...s, ...d.search } : s)));
      setActive((prev) => (prev && prev.id === id ? { ...prev, ...d.search } : prev));
    } catch { /* ignore */ }
    finally { setDetailLoading(false); }
  };

  useEffect(() => { refreshList(); /* eslint-disable-next-line */ }, [exhibitionId, sharedSearch.bumpCounter]);
  useEffect(() => { if (active?.id) loadDetails(active.id); /* eslint-disable-next-line */ }, [active?.id]);

  // Poll while ANY search is running OR qualifying. Reloads the active search's
  // results once its qualification finishes so the score column lights up.
  useEffect(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const anyRunning = searches.some((s) => s.status === 'running');
    const anyQualifying = searches.some((s) => s.qualification_status === 'qualifying');
    if (anyRunning || anyQualifying) {
      timerRef.current = setTimeout(async () => {
        await refreshList();
        if (active?.id) {
          await loadDetails(active.id);
        }
      }, LIST_POLL_MS);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searches, active]);

  // Dismiss any in-flight qualify loading toasts on unmount so they don't
  // linger forever if the curator navigates away mid-qualification.
  useEffect(() => () => {
    Object.values(qualifyToastsRef.current).forEach((t) => {
      try { toast.dismiss(t.toastId); } catch { /* ignore */ }
    });
    qualifyToastsRef.current = {};
  }, []);

  // React to qualification_status transitions and swap the persistent toast
  // for a terminal success / failure message.
  useEffect(() => {
    searches.forEach((s) => {
      const tracker = qualifyToastsRef.current[s.id];
      if (!tracker) return;
      const status = s.qualification_status;
      if (status === tracker.lastStatus) return;
      tracker.lastStatus = status;
      if (status === 'completed') {
        const summary = s.qualification_summary || {};
        const parts = [];
        if (summary.vip) parts.push(`${summary.vip} VIP`);
        if (summary.high) parts.push(`${summary.high} high`);
        if (summary.duplicates) parts.push(`${summary.duplicates} dup`);
        if (summary.suppressed) parts.push(`${summary.suppressed} suppressed`);
        if (summary.average_score != null) parts.push(`avg ${summary.average_score}`);
        const updated = summary.updated || 0;
        toast.success(
          `Qualification complete · ${updated} updated${parts.length ? ' · ' + parts.join(' · ') : ''}`,
          { id: tracker.toastId },
        );
        if (summary.ai_ok === false && summary.ai_error) {
          toast.message('Deterministic dedupe only', { description: summary.ai_error });
        }
        delete qualifyToastsRef.current[s.id];
        if (active?.id === s.id) loadDetails(s.id);
      } else if (status === 'failed') {
        toast.error(
          `Qualification failed${s.qualification_error_message ? ': ' + s.qualification_error_message : ''}`,
          { id: tracker.toastId },
        );
        delete qualifyToastsRef.current[s.id];
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searches]);

  const qualify = async () => {
    if (!active?.id) return;
    if (active.qualification_status === 'qualifying') {
      toast.message('Qualification is already running.');
      return;
    }
    if (active.status !== 'completed') {
      toast.error('Search must be completed before qualifying.');
      return;
    }
    const toastId = toast.loading('Qualifying prospects… AI processing can take a moment.');
    qualifyToastsRef.current[active.id] = { toastId, lastStatus: 'qualifying' };
    try {
      await inviteApi.qualifySearch(active.id);
      // Immediately refresh so the QUALIFYING badge appears without waiting
      // for the next poll tick.
      await refreshList();
      if (active.id) await loadDetails(active.id);
    } catch (err) {
      delete qualifyToastsRef.current[active.id];
      toast.error(errorDetail(err, 'Qualify failed'), { id: toastId });
    }
  };

  const cancel = async (id) => {
    try {
      await inviteApi.cancelSearch(id);
      toast.message('Search cancelled');
      if (sharedSearch.activeSearchId === id) await sharedSearch.refresh();
      await refreshList();
      if (active?.id === id) await loadDetails(id);
    } catch (err) { toast.error(errorDetail(err, 'Cancel failed')); }
  };
  const retry = async (s) => {
    try {
      const res = await inviteApi.createSearch({
        exhibition_id: exhibitionId,
        audience_type: s.audience_type,
        location_query: s.location_query || '',
        search_angle: s.search_angle || '',
        requested_limit: s.requested_limit || 20,
        admin_note: '',
      });
      toast.success('Retry search started');
      sharedSearch.start(res.search_id);
      await refreshList();
    } catch (err) { toast.error(errorDetail(err, 'Retry failed')); }
  };

  const rowAction = async (resultId, kind, fn, okMsg) => {
    setRowBusy((prev) => ({ ...prev, [resultId]: kind }));
    const loadingToast = toast.loading(kind === 'moving' ? 'Moving to Prospect…' : 'Converting to invite…');
    try {
      await fn();
      toast.success(okMsg, { id: loadingToast });
      await loadDetails(active.id);
    } catch (err) {
      toast.error(errorDetail(err, 'Action failed'), { id: loadingToast });
    } finally {
      setRowBusy((prev) => { const cp = { ...prev }; delete cp[resultId]; return cp; });
    }
  };

  const moveAll = async ({ include_duplicates = false, include_suppressed = false } = {}) => {
    if (!active?.id) return;
    const loadingToast = toast.loading('Moving all to Prospects…');
    try {
      const r = await inviteApi.saveProspectsBulk(active.id, { include_duplicates, include_suppressed });
      setMoveAllResult(r);
      toast.success(`${r.saved_count} moved · ${r.skipped_count} skipped`, { id: loadingToast });
      await loadDetails(active.id);
    } catch (err) {
      toast.error(errorDetail(err, 'Move All failed'), { id: loadingToast });
    }
  };

  // Sort results: highest score → priority → duplicate_status → status
  const sortedResults = (() => {
    if (!details?.results?.length) return [];
    const rows = [...details.results];
    rows.sort((a, b) => {
      const aTerm = (a.status === 'rejected' || a.status === 'archived') ? 1 : 0;
      const bTerm = (b.status === 'rejected' || b.status === 'archived') ? 1 : 0;
      if (aTerm !== bTerm) return aTerm - bTerm;
      const aScore = scoreOf(a);
      const bScore = scoreOf(b);
      if (aScore !== bScore) return bScore - aScore;
      const aPri = PRIORITY_RANK[priorityOf(a)] || 0;
      const bPri = PRIORITY_RANK[priorityOf(b)] || 0;
      if (aPri !== bPri) return bPri - aPri;
      const aDup = DUP_RANK[a.duplicate_status] ?? 3;
      const bDup = DUP_RANK[b.duplicate_status] ?? 3;
      return bDup - aDup;
    });
    return rows;
  })();

  // Sort searches running-first
  const sortedSearches = [...searches].sort((a, b) => {
    if (a.status === 'running' && b.status !== 'running') return -1;
    if (b.status === 'running' && a.status !== 'running') return 1;
    return 0;
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="search-results-tab">
      {/* Search list */}
      <div className="border border-[#1a1a1a] bg-[#0a0a0a]">
        <div className="px-4 py-3 border-b border-[#1a1a1a] flex items-center justify-between">
          <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#777]">Searches</p>
          <button onClick={refreshList} data-clickable="true" className="text-[#666] hover:text-[#90E0EF]" aria-label="Refresh"><RefreshCcw className="w-3.5 h-3.5" /></button>
        </div>
        <div className="max-h-[68vh] overflow-y-auto">
          {loading && <p className="p-4 text-xs font-mono text-[#555]">Loading…</p>}
          {!loading && !searches.length && <p className="p-4 text-xs font-mono text-[#555]">No searches yet.</p>}
          {sortedSearches.map((s) => {
            const isRunning = s.status === 'running';
            const pct = Math.min(100, Math.max(0, s.progress_percent || 0));
            const isQualifying = s.qualification_status === 'qualifying';
            const qPct = Math.min(100, Math.max(0, s.qualification_progress_percent || 0));
            return (
              <button
                key={s.id}
                onClick={() => setActive(s)}
                data-clickable="true"
                data-testid={`search-row-${s.id}`}
                className={`block w-full text-left px-4 py-3 border-b border-[#0e0e0e] hover:bg-[#0c0c0c] transition-colors ${active?.id === s.id ? 'bg-[#0d0d0d] border-l-2 border-l-[#90E0EF]' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-mono text-white truncate flex items-center gap-1.5">
                    {(isRunning || isQualifying) && <Loader2 className="w-3 h-3 animate-spin text-[#90E0EF]" />}
                    {s.audience_type} · {s.location_query || '—'}
                  </span>
                  {isQualifying
                    ? <span className="text-[9px] font-mono px-1.5 py-0.5 border border-[#90E0EF]/40 text-[#90E0EF] uppercase tracking-[0.14em]">Qualifying</span>
                    : <InviteStatusBadge status={s.is_stuck ? 'paused' : s.status} />}
                </div>
                <p className="text-[10px] font-mono text-[#666] mt-1 line-clamp-1">{s.search_angle || '—'}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] font-mono text-[#555]">
                    {isRunning
                      ? `${s.elapsed_seconds}s · ${pct}%`
                      : isQualifying
                        ? `Qualifying · ${s.qualification_elapsed_seconds || 0}s · ${qPct}%`
                        : `${s.result_count} results · ${new Date(s.created_at).toLocaleDateString()}`}
                  </span>
                </div>
                {(isRunning || isQualifying) && (
                  <div className="h-0.5 bg-[#111] mt-2 overflow-hidden">
                    <motion.div
                      animate={{ width: `${isRunning ? pct : qPct}%` }}
                      transition={{ duration: 0.6 }}
                      className="h-full bg-[#90E0EF]"
                      style={{ boxShadow: '0 0 8px rgba(144,224,239,0.4)' }}
                    />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Result list */}
      <div className="lg:col-span-2 border border-[#1a1a1a] bg-[#0a0a0a] flex flex-col">
        <div className="px-4 py-3 border-b border-[#1a1a1a] flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#777]">
            {active ? `${active.audience_type} · ${active.location_query || 'any region'}` : 'Pick a search'}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            {active?.status === 'running' && (
              <button onClick={() => cancel(active.id)} data-clickable="true" data-testid="result-pane-cancel" className="text-[10px] font-mono px-3 py-1.5 border border-[#333] text-[#aaa] hover:text-white uppercase tracking-[0.14em] inline-flex items-center gap-1">
                <XCircle className="w-3 h-3"/> Cancel
              </button>
            )}
            {(active?.status === 'failed' || active?.status === 'cancelled') && (
              <button onClick={() => retry(active)} data-clickable="true" data-testid="result-pane-retry" className="text-[10px] font-mono px-3 py-1.5 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 uppercase tracking-[0.14em] inline-flex items-center gap-1">
                <RotateCw className="w-3 h-3"/> Retry
              </button>
            )}
            {active?.status === 'completed' && (
              <>
                <button onClick={() => setShowMoveAll(true)} data-clickable="true" data-testid="search-move-all" className="text-[10px] font-mono px-3 py-1.5 border border-[rgba(200,157,255,0.4)] text-[#c89dff] hover:bg-[rgba(200,157,255,0.07)] uppercase tracking-[0.14em] inline-flex items-center gap-1">
                  <BookmarkPlus className="w-3 h-3"/> Move all to Prospects
                </button>
                {(() => {
                  const q = active.qualification_status;
                  const isQualifying = q === 'qualifying';
                  const isFailedQ = q === 'failed';
                  const label = isQualifying
                    ? 'Qualifying…'
                    : (isFailedQ ? 'Retry Qualify / Dedupe' : 'Qualify / Dedupe');
                  return (
                    <button
                      onClick={qualify}
                      disabled={isQualifying}
                      data-clickable="true"
                      data-testid="search-qualify-btn"
                      className={`text-[10px] font-mono px-3 py-1.5 border uppercase tracking-[0.14em] inline-flex items-center gap-1 ${
                        isQualifying
                          ? 'border-[#333] text-[#666] cursor-not-allowed'
                          : isFailedQ
                            ? 'border-[rgba(232,139,160,0.5)] text-[#e88ba0] hover:bg-[rgba(232,139,160,0.06)]'
                            : 'border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10'
                      }`}
                    >
                      {isQualifying && <Loader2 className="w-3 h-3 animate-spin"/>}
                      {isFailedQ && !isQualifying && <RotateCw className="w-3 h-3"/>}
                      {label}
                    </button>
                  );
                })()}
                {active.qualification_status === 'qualifying' && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 border border-[#90E0EF]/30 text-[#90E0EF] uppercase tracking-[0.14em] bg-[rgba(144,224,239,0.06)]" data-testid="search-qualifying-badge">
                    <Loader2 className="w-3 h-3 animate-spin"/> Qualifying · {active.qualification_progress_percent || 0}%
                  </span>
                )}
                {active.qualification_status === 'failed' && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 border border-[rgba(232,139,160,0.45)] text-[#e88ba0] uppercase tracking-[0.14em]" data-testid="search-qualify-failed-badge">
                    Failed qualification
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {active?.qualification_status === 'qualifying' && (
          <div className="px-4 py-2 border-b border-[#161616] bg-[#070707]" data-testid="search-qualify-progress">
            <div className="flex items-center justify-between text-[10px] font-mono text-[#777] mb-1.5">
              <span className="text-[#90E0EF] flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin"/> Qualifying · {active.qualification_elapsed_seconds || 0}s elapsed · ~{active.qualification_estimated_seconds || 60}s est.
              </span>
              <span>{active.qualification_progress_percent || 0}%</span>
            </div>
            <div className="h-1 bg-[#111] overflow-hidden">
              <motion.div
                animate={{ width: `${active.qualification_progress_percent || 0}%` }}
                transition={{ duration: 0.6 }}
                className="h-full bg-[#90E0EF]"
                style={{ boxShadow: '0 0 10px rgba(144,224,239,0.45)' }}
              />
            </div>
            <p className="text-[11px] font-mono text-[#888] mt-2">AI processing can take a moment. You can keep working — the Invite OS stays responsive.</p>
          </div>
        )}

        {active?.qualification_status === 'failed' && (
          <div className="px-4 py-3 border-b border-[#161616] bg-[#0c0606]" data-testid="search-qualify-failed">
            <p className="text-sm font-outfit text-[#e88ba0]">Qualification failed.</p>
            {active.qualification_error_message && (
              <p className="text-[11px] font-mono text-[#aaa] leading-relaxed break-words mt-1">{active.qualification_error_message}</p>
            )}
            <p className="text-[10px] font-mono text-[#666] mt-2">Press the Retry button above to try again.</p>
          </div>
        )}

        {active && active.status !== 'completed' && (
          <div className="px-4 py-3 border-b border-[#161616]">
            {active.status === 'running' && (
              <>
                <div className="flex items-center justify-between text-[10px] font-mono text-[#777] mb-1.5">
                  <span>{active.elapsed_seconds}s elapsed · ~{active.estimated_seconds}s est.</span>
                  <span>{active.progress_percent}%</span>
                </div>
                <div className="h-1 bg-[#111] overflow-hidden">
                  <motion.div animate={{ width: `${active.progress_percent || 0}%` }} transition={{ duration: 0.6 }} className="h-full bg-[#90E0EF]" style={{ boxShadow: '0 0 10px rgba(144,224,239,0.45)' }} />
                </div>
                <p className="text-[11px] font-mono text-[#888] mt-2">Search is running. Existing invite data stays available — feel free to keep working.</p>
              </>
            )}
            {active.status === 'failed' && (
              <div>
                <p className="text-sm font-outfit text-[#e88ba0]">Search failed.</p>
                {active.error_message && <p className="text-[11px] font-mono text-[#aaa] leading-relaxed break-words mt-1">{active.error_message}</p>}
              </div>
            )}
            {active.status === 'cancelled' && <p className="text-sm font-outfit text-[#aaa]">Cancelled.</p>}
          </div>
        )}

        <div className="overflow-x-auto max-h-[68vh] overflow-y-auto">
          <table className="w-full text-[11px] font-mono">
            <thead className="text-[#666] uppercase tracking-[0.14em] sticky top-0 bg-[#0a0a0a]">
              <tr className="border-b border-[#161616]">
                <th className="text-left p-2.5 w-12">Score</th>
                <th className="text-left p-2.5">Name</th>
                <th className="text-left p-2.5">Org</th>
                <th className="text-left p-2.5">Email</th>
                <th className="text-left p-2.5">Pri</th>
                <th className="text-left p-2.5">Dup</th>
                <th className="text-left p-2.5">Action</th>
                <th className="text-left p-2.5">Status</th>
                <th className="text-right p-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {detailLoading && <tr><td colSpan={9} className="p-4 text-[#555]">Loading…</td></tr>}
              {!detailLoading && !sortedResults.length && (
                <tr><td colSpan={9} className="p-4 text-[#555]">
                  {active?.status === 'running' ? 'Results will appear here once the search completes.' : 'No prospects in this search.'}
                </td></tr>
              )}
              {sortedResults.map((r) => (
                <ResultRow key={r.id} r={r} busy={rowBusy[r.id]} onOpen={() => setSelectedResult(r)} onAction={rowAction} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {selectedResult && (
          <SearchResultProfile
            result={selectedResult}
            onClose={() => setSelectedResult(null)}
            onChanged={() => loadDetails(active?.id)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMoveAll && (
          <MoveAllModal
            onClose={() => { setShowMoveAll(false); setMoveAllResult(null); }}
            onSubmit={moveAll}
            result={moveAllResult}
            search={active}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ResultRow({ r, onOpen, onAction, busy }) {
  const dupSuppressed = r.duplicate_status === 'suppressed';
  const alreadySaved = r.status === 'saved';
  const alreadyConverted = r.status === 'converted';
  const score = scoreOf(r);
  const priority = priorityOf(r);
  const recAction = recActionOf(r);
  const hasRisk = !!(r.risk_notes && r.risk_notes.trim());

  return (
    <tr
      className="border-b border-[#0e0e0e] hover:bg-[#0c0c0c] cursor-pointer transition-colors group"
      data-testid={`result-row-${r.id}`}
      onClick={onOpen}
    >
      <td className="p-2.5 align-top">
        <ScoreBadge score={score} />
      </td>
      <td className="p-2.5 text-white align-top">
        <div className="flex items-center gap-1.5">
          <span>{r.name || '—'}</span>
          {hasRisk && <span title={r.risk_notes} className="block w-1.5 h-1.5 rounded-full bg-[#e4c07a]" />}
          {busy && <span className="inline-flex items-center gap-1 text-[9px] font-mono text-[#90E0EF]"><Loader2 className="w-3 h-3 animate-spin"/></span>}
        </div>
      </td>
      <td className="p-2.5 text-[#aaa] align-top">{r.organization_name || '—'}</td>
      <td className="p-2.5 text-[#888] align-top truncate max-w-[180px]">{r.email || '—'}</td>
      <td className="p-2.5 align-top"><InviteStatusBadge status={priority} /></td>
      <td className="p-2.5 align-top">{r.duplicate_status && r.duplicate_status !== 'new' ? <InviteStatusBadge status={r.duplicate_status} /> : <span className="text-[#444]">—</span>}</td>
      <td className="p-2.5 align-top">{recAction ? <InviteStatusBadge status={recAction} /> : <span className="text-[#444]">—</span>}</td>
      <td className="p-2.5 align-top"><InviteStatusBadge status={r.status} /></td>
      <td className="p-2.5 text-right align-top" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1.5">
          <IconBtn icon={Eye} label="View" onClick={onOpen} testid={`row-view-${r.id}`} />
          {!alreadyConverted && !alreadySaved && !dupSuppressed && (
            <IconBtn
              icon={busy === 'moving' ? Loader2 : BookmarkPlus}
              spinning={busy === 'moving'}
              label="Move to Prospect"
              tone="violet"
              onClick={() => onAction(r.id, 'moving', () => inviteApi.saveProspect(r.id), 'Moved to Prospects')}
              testid={`row-move-${r.id}`}
              disabled={!!busy}
            />
          )}
          {!alreadyConverted && !dupSuppressed && (
            <IconBtn
              icon={busy === 'converting' ? Loader2 : Send}
              spinning={busy === 'converting'}
              label="Convert to Invite"
              tone="cyan"
              onClick={() => onAction(r.id, 'converting', () => inviteApi.convertSearchResult(r.id), 'Converted')}
              testid={`row-convert-${r.id}`}
              disabled={!!busy}
            />
          )}
          {dupSuppressed && (
            <span className="text-[9px] font-mono text-[#e88ba0] tracking-[0.14em] uppercase inline-flex items-center gap-1"><ShieldOff className="w-3 h-3"/>Suppressed</span>
          )}
          {alreadyConverted && (
            <span className="text-[9px] font-mono text-[#7ed4a0] tracking-[0.14em] uppercase">Converted</span>
          )}
          {alreadySaved && !alreadyConverted && (
            <span className="text-[9px] font-mono text-[#c89dff] tracking-[0.14em] uppercase">In Prospects</span>
          )}
        </div>
      </td>
    </tr>
  );
}

function ScoreBadge({ score }) {
  if (score == null) return <span className="text-[10px] font-mono text-[#444]">—</span>;
  let style;
  if (score >= 90) style = { color: '#90E0EF', border: 'rgba(144,224,239,0.5)', bg: 'rgba(144,224,239,0.1)' };
  else if (score >= 75) style = { color: '#7ed4a0', border: 'rgba(126,212,160,0.45)', bg: 'rgba(126,212,160,0.08)' };
  else if (score >= 55) style = { color: '#cccccc', border: '#333', bg: 'rgba(255,255,255,0.03)' };
  else if (score >= 35) style = { color: '#e4c07a', border: 'rgba(228,192,122,0.45)', bg: 'rgba(228,192,122,0.07)' };
  else style = { color: '#e88ba0', border: 'rgba(232,139,160,0.45)', bg: 'rgba(232,139,160,0.07)' };
  return (
    <span className="inline-flex items-center justify-center text-[10px] font-mono px-2 py-0.5 border tabular-nums font-semibold" style={{ color: style.color, borderColor: style.border, background: style.bg, minWidth: 32 }}>
      {score}
    </span>
  );
}

function scoreOf(r) {
  const raw = r.raw_ai_json || {};
  if (typeof raw === 'object' && raw && raw.qualification_score != null) {
    const n = Number(raw.qualification_score);
    if (Number.isFinite(n)) return Math.max(0, Math.min(100, Math.round(n)));
  }
  return null;
}
function priorityOf(r) {
  const raw = r.raw_ai_json || {};
  if (typeof raw === 'object' && raw && raw.priority) return String(raw.priority).toLowerCase();
  return null;
}
function recActionOf(r) {
  const raw = r.raw_ai_json || {};
  if (typeof raw === 'object' && raw && raw.recommended_action) return String(raw.recommended_action).toLowerCase();
  return null;
}

function IconBtn({ icon: Icon, label, onClick, tone = 'muted', testid, disabled = false, spinning = false }) {
  const map = {
    cyan: 'border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10',
    violet: 'border-[rgba(200,157,255,0.4)] text-[#c89dff] hover:bg-[rgba(200,157,255,0.07)]',
    muted: 'border-[#222] text-[#888] hover:text-white hover:border-[#444]',
  };
  return (
    <button onClick={onClick} title={label} aria-label={label} data-clickable="true" data-testid={testid} disabled={disabled} className={`p-1.5 border transition-colors ${disabled ? 'opacity-40 cursor-not-allowed' : map[tone]}`}>
      <Icon className={`w-3 h-3 ${spinning ? 'animate-spin' : ''}`}/>
    </button>
  );
}

function MoveAllModal({ onClose, onSubmit, result, search }) {
  const [includeDup, setIncludeDup] = useState(false);
  const [includeSup, setIncludeSup] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try { await onSubmit({ include_duplicates: includeDup, include_suppressed: includeSup }); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-[#222] w-full max-w-lg p-6 space-y-4" data-testid="move-all-modal">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#c89dff]">Move all to Prospects</p>
          <button onClick={onClose} className="text-[#777] hover:text-white"><X className="w-4 h-4"/></button>
        </div>
        {!result && (
          <>
            <p className="text-xs font-mono text-[#aaa] leading-relaxed">
              This will move eligible results from the search
              <span className="text-white"> {search?.audience_type} · {search?.location_query || 'any region'} </span>
              into Prospects. Suppressed, rejected, and already-converted records will be skipped.
            </p>
            <div className="space-y-2 text-[11px] font-mono">
              <label className="flex items-center gap-2 text-[#aaa] cursor-pointer">
                <input type="checkbox" checked={includeDup} onChange={(e) => setIncludeDup(e.target.checked)} data-testid="move-all-include-dup" />
                Include "already invited / ticketed" duplicates
              </label>
              <label className="flex items-center gap-2 text-[#aaa] cursor-pointer">
                <input type="checkbox" checked={includeSup} onChange={(e) => setIncludeSup(e.target.checked)} data-testid="move-all-include-sup" />
                Include suppressed addresses (not recommended)
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-[#1a1a1a]">
              <button onClick={onClose} className="text-[10px] font-mono px-3 py-2 border border-[#333] text-[#aaa] hover:text-white uppercase tracking-[0.14em]">Cancel</button>
              <button onClick={submit} disabled={busy} data-clickable="true" data-testid="move-all-submit" className={`text-[10px] font-mono px-3 py-2 border uppercase tracking-[0.14em] inline-flex items-center gap-1.5 ${busy ? 'border-[#333] text-[#666] cursor-not-allowed' : 'border-[rgba(200,157,255,0.45)] text-[#c89dff] hover:bg-[rgba(200,157,255,0.08)]'}`}>
                {busy && <Loader2 className="w-3 h-3 animate-spin"/>}{busy ? 'Moving…' : 'Move now'}
              </button>
            </div>
          </>
        )}
        {result && (
          <div className="space-y-3">
            <p className="text-sm font-outfit text-white">{result.saved_count} moved · {result.skipped_count} skipped</p>
            {!!result.skipped?.length && (
              <div className="border border-[#1a1a1a] max-h-[40vh] overflow-y-auto">
                {result.skipped.map((s, i) => (
                  <div key={i} className="px-3 py-1.5 border-b border-[#0e0e0e] text-[10px] font-mono text-[#888] flex items-center justify-between gap-2">
                    <span className="truncate">{s.result_id}</span>
                    <span className="text-[#666] flex-shrink-0">{s.reason}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={onClose} data-clickable="true" data-testid="move-all-close" className="text-[10px] font-mono px-3 py-2 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 uppercase tracking-[0.14em]">Done</button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function errorDetail(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.detail || d.message || fallback;
  return err?.message || fallback;
}
